import React, { useEffect, useMemo, useState, useContext } from "react";
import { Modal, Input, Form, Button, Typography, InputNumber, Select, Space, Checkbox, Divider, AutoComplete } from "antd";
import dayjs from "dayjs";
import { toast } from "react-toastify";
import { api } from "../../config/api";
import { CurrencyContext } from "../../context/CurrencyContext";
import { getUsers } from "../../lib/user";
import { getQuote } from "../../lib/quotes";
import { HiOutlinePencilSquare } from "react-icons/hi2";
import DiscountInput from "../forms/helperComponent/DiscountInput";

const { Title, Text } = Typography;
const ORDER_REFERENCE_MAX_LENGTH = 60;
const DESCRIPTION_MAX_LENGTH = 200;

// Helper to add calendar days (include weekends)
const addCalendarDays = (startDateStr, days) => {
  return dayjs(startDateStr).add(Number(days || 0), "day");
};

const resolveStatus = (total, paid, currentStatus) => {
  const absTotal = Math.abs(Number(total || 0));
  const absPaid = Math.abs(Number(paid || 0));
  const absRemaining = Math.max(0, Number((absTotal - absPaid).toFixed(2)));

  if (absPaid > 0 && absRemaining > 0) return "Partially Paid";
  if (absRemaining <= 0 && (absPaid > 0 || absTotal > 0)) return "Paid";
  if (absPaid === 0) return "Not Paid";
  return currentStatus || "Not Paid";
};

const EditInvoiceModal = ({ open, onClose, invoice, onUpdated }) => {
  const [form] = Form.useForm();
  const [submitting, setSubmitting] = useState(false);
  const [cifValue, setCifValue] = useState(0);
  const [nameOptions, setNameOptions] = useState([]);
  const [loadingNameOptions, setLoadingNameOptions] = useState(false);
  const [invoiceDiscount, setInvoiceDiscount] = useState(null);
  const [discountType, setDiscountType] = useState("percentage");
  const [discountValue, setDiscountValue] = useState("");
  const itemsWatch = Form.useWatch('items', form);
  const vatWatch = Form.useWatch('vatPercentage', form);
  const insuranceWatch = Form.useWatch('isInsuranceChecked', form);
  const statusWatch = Form.useWatch('status', form);
  const totalWatch = Form.useWatch('total', form);
  const paidWatch = Form.useWatch('paid', form);
  const currencyWatch = Form.useWatch('currency', form);
  const isCreditInvoice = Boolean(
    invoice?.isCreditInvoice ||
    invoice?.documentType === 'credit' ||
    Number(invoice?.total) < 0 ||
    Number(invoice?.remaining) < 0 ||
    invoice?.items?.some((item) => Number(item?.qty) < 0 || Number(item?.price) < 0) ||
    itemsWatch?.some((item) => Number(item?.qty) < 0 || Number(item?.price) < 0)
  );
  const canEditName = Boolean(
    String(invoice?.name || "").trim() &&
    (
      invoice?.isExternalInvoice ||
      invoice?.isImportInvoice ||
      ["external", "import"].includes(invoice?.invoiceSource) ||
      !invoice?.userId
    )
  );

  const initialValues = useMemo(() => {
    if (!invoice) return {};
    return {
      name: invoice.name || "",
      invoiceDate: invoice.invoiceDate || dayjs().format("YYYY-MM-DD"),
      dueDays: invoice.dueDays ?? "",
      invoiceNo: invoice.invoiceNo || "",
      orderReference: invoice.orderReference || "",
      collectionLocation: invoice.collectionLocation || "",
      deliveryLocation: invoice.deliveryLocation || "",
      billToText: invoice.serviceRecipientText || invoice.billToText || "",
      serviceRecipientText: invoice.serviceRecipientText || "",
      externalBillToText: invoice.externalBillToText || "",
      description: invoice.description || "",
      total: invoice.total ?? undefined,
      paid: invoice.paid ?? 0,
      status: resolveStatus(invoice.total, invoice.paid, invoice.status),
      items: invoice.items && invoice.items.length ? invoice.items : [],
      vatPercentage: typeof invoice.vatPercentage === 'number' ? invoice.vatPercentage : undefined,
      vatAmount: typeof invoice.vatAmount === 'number' ? invoice.vatAmount : undefined,
      isInsuranceChecked: !!invoice.isInsuranceChecked,
      insuranceAmount: typeof invoice.insuranceAmount === 'number' ? invoice.insuranceAmount : 0,
      currency: invoice.currency || "USD",
    };
  }, [invoice]);

  useEffect(() => {
    if (!open || !canEditName) return;

    let cancelled = false;

    const fetchNameOptions = async () => {
      setLoadingNameOptions(true);
      try {
        const [firestoreUsersResult, invoicesResult] = await Promise.allSettled([
          getUsers(),
          api.get("/invoice"),
        ]);
        const namesByKey = new Map();
        const addName = (rawName) => {
          const name = String(rawName || "").trim();
          if (!name) return;
          const key = name.toLowerCase();
          if (!namesByKey.has(key)) namesByKey.set(key, name);
        };

        if (firestoreUsersResult.status === "fulfilled") {
          (firestoreUsersResult.value || []).forEach((user) => addName(user?.name));
        } else {
          console.warn("Failed to fetch Firebase names:", firestoreUsersResult.reason);
        }

        if (invoicesResult.status === "fulfilled") {
          const invoices = invoicesResult.value?.data;
          (Array.isArray(invoices) ? invoices : []).forEach((item) => addName(item?.name));
        } else {
          console.warn("Failed to fetch MongoDB invoice names:", invoicesResult.reason);
        }

        if (!cancelled) {
          setNameOptions(
            Array.from(namesByKey.values())
              .sort((a, b) => a.localeCompare(b))
              .map((name) => ({ value: name, label: name }))
          );
        }
      } finally {
        if (!cancelled) setLoadingNameOptions(false);
      }
    };

    fetchNameOptions();
    return () => {
      cancelled = true;
    };
  }, [open, canEditName]);

  useEffect(() => {
    (async () => {
      if (open && invoice) {
        let fresh = invoice;
        try {
          // Refresh from server by invoiceNo when available, otherwise use the invoice _id
          if (invoice?.invoiceNo) {
            const resp = await api.get(`/invoice/search`, { params: { invoiceNo: String(invoice.invoiceNo).trim() } });
            fresh = resp?.data || invoice;
          } else if (invoice?._id) {
            const resp = await api.get(`/invoice/${invoice._id}`);
            fresh = resp?.data || invoice;
          }

          form.setFieldsValue({
            name: fresh.name || invoice.name || "",
            invoiceDate: fresh.invoiceDate || dayjs().format("YYYY-MM-DD"),
            dueDays: fresh.dueDays ?? "",
            invoiceNo: fresh.invoiceNo || "",
            orderReference: fresh.orderReference || "",
            collectionLocation: fresh.collectionLocation || "",
            deliveryLocation: fresh.deliveryLocation || "",
            billToText: fresh.serviceRecipientText || fresh.billToText || "",
            serviceRecipientText: fresh.serviceRecipientText || "",
            externalBillToText: fresh.externalBillToText || "",
            description: fresh.description || "",
            total: fresh.total ?? undefined,
            paid: fresh.paid ?? 0,
            status: resolveStatus(fresh.total ?? invoice.total, fresh.paid ?? invoice.paid, fresh.status || invoice.status),
            items: fresh.items && fresh.items.length ? fresh.items : [],
            vatPercentage: typeof fresh.vatPercentage === 'number' ? fresh.vatPercentage : undefined,
            vatAmount: typeof fresh.vatAmount === 'number' ? fresh.vatAmount : undefined,
            isInsuranceChecked: !!fresh.isInsuranceChecked,
            insuranceAmount: typeof fresh.insuranceAmount === 'number' ? fresh.insuranceAmount : 0,
            currency: fresh.currency || "USD",
          });
          // Use CIF value from the invoice collection when available
          if (fresh?.cifValue !== undefined && fresh?.cifValue !== null) {
            setCifValue(Number(fresh.cifValue) || 0);
          }
        } catch (_) {
          form.setFieldsValue(initialValues);
          if (invoice?.cifValue !== undefined && invoice?.cifValue !== null) {
            setCifValue(Number(invoice.cifValue) || 0);
          }
        }

        // Determine whether discount was explicitly set (including null/removed) on the invoice
        let currentDiscount = null;
        let discountExplicitlySet = false;

        if (fresh && Object.prototype.hasOwnProperty.call(fresh, "discount")) {
          currentDiscount = fresh.discount;
          discountExplicitlySet = true;
        } else if (fresh?.discount !== undefined) {
          currentDiscount = fresh.discount;
          discountExplicitlySet = true;
        } else if (invoice && Object.prototype.hasOwnProperty.call(invoice, "discount")) {
          currentDiscount = invoice.discount;
          discountExplicitlySet = true;
        } else if (invoice?.discount !== undefined) {
          currentDiscount = invoice.discount;
          discountExplicitlySet = true;
        }

        // Prefill items from shipment if no custom items are stored
        const current = form.getFieldValue("items");
        if (invoice?.shipmentId) {
          try {
            const quote = await getQuote(invoice.shipmentId);
            if (!discountExplicitlySet && !currentDiscount) {
              currentDiscount = quote?.discount || quote?.tradeOSData?.discount || null;
            }
            if ((!current || current.length === 0) && quote?.services?.length) {
              const mapped = quote.services.map(s => ({
                description: s?.description || "",
                qty: Number(s?.numberOfItem || 1),
                price: Number(s?.exclVAT || 0),
              }));
              form.setFieldsValue({ items: mapped });
            }
            if ((!current || current.length === 0) && quote?.vatPercentage !== undefined) {
              form.setFieldsValue({ vatPercentage: Number(quote.vatPercentage) });
            }
            if ((!current || current.length === 0) && quote?.vatAmount !== undefined) {
              form.setFieldsValue({ vatAmount: Number(quote.vatAmount) });
            }
            if (quote?.cifValue?.value && !cifValue) {
              setCifValue(Number(quote.cifValue.value));
            }
            if ((!current || current.length === 0) && typeof quote?.isInsuranceChecked === 'boolean') {
              form.setFieldsValue({ isInsuranceChecked: quote.isInsuranceChecked });
            }
            if ((!current || current.length === 0) && quote?.insurance) {
              form.setFieldsValue({ insuranceAmount: Number(quote.insurance) });
            }
          } catch (_) {
            // ignore prefill failures
          }
        }
        const hasValidDiscount = !isCreditInvoice && currentDiscount && (
          (Number(currentDiscount.value) > 0) || (Number(currentDiscount.amount) > 0)
        );
        if (hasValidDiscount) {
          setDiscountType(currentDiscount.type || "percentage");
          setDiscountValue(
            currentDiscount.value !== undefined && currentDiscount.value !== null
              ? String(currentDiscount.value)
              : (currentDiscount.amount ? String(currentDiscount.amount) : "")
          );
          setInvoiceDiscount(currentDiscount);
        } else {
          setDiscountType("percentage");
          setDiscountValue("");
          setInvoiceDiscount(null);
        }
      }
    })();
  }, [open, invoice, form, initialValues]);

  const { currencies } = useContext(CurrencyContext);
  const currencyOptions = useMemo(() => {
    if (!currencies || !currencies.length) return [{ value: "USD", label: "USD" }];
    return currencies.map((c) => ({ value: c.name, label: `${c.name} ${c.symbol ? `(${c.symbol})` : ""}` }));
  }, [currencies]);

  // Recalculate totals in real time when items/VAT/insurance/discount change
  useEffect(() => {
    const items = itemsWatch || [];
    const creditInvoice = isCreditInvoice || items.some(
      (item) => Number(item?.qty) < 0 || Number(item?.price) < 0
    );
    const isTaxOrDutyItem = (description) => {
      const desc = String(description || "").trim().toLowerCase();
      return (
        desc === "vat" ||
        desc.includes("duty") ||
        desc.includes("duties") ||
        desc.includes("tax")
      );
    };

    let discountableSubTotal = 0;
    const subTotal = items.reduce((sum, it) => {
      const qty = Number(it?.qty || 0);
      const price = Number(it?.price || 0);
      const lineTotal = creditInvoice ? Math.abs(qty) * Math.abs(price) : qty * price;
      if (!isTaxOrDutyItem(it?.description)) {
        discountableSubTotal += lineTotal;
      }
      return sum + lineTotal;
    }, 0);

    const rawDiscount = creditInvoice ? 0 : (parseFloat(discountValue) || 0);
    let discountAmt = 0;
    if (rawDiscount > 0) {
      if (discountType === "percentage") {
        discountAmt = (discountableSubTotal * rawDiscount) / 100;
      } else {
        discountAmt = rawDiscount;
      }
      discountAmt = Math.min(discountAmt, discountableSubTotal);
    }
    const updatedDiscount = !creditInvoice && discountAmt > 0 ? {
      type: discountType,
      value: rawDiscount,
      amount: parseFloat(discountAmt.toFixed(2)),
      servicesSubtotalBeforeDiscount: discountableSubTotal,
    } : null;
    setInvoiceDiscount(updatedDiscount);

    const vatPct = Number(vatWatch || 0);
    const vatAmount = vatPct > 0 ? Number(((subTotal * vatPct) / 100).toFixed(2)) : 0;
    // Insurance on 0.75% CIF value. Fallback to items subtotal when CIF not available.
    const baseForInsurance = Number(cifValue || 0) > 0 ? Number(cifValue) : Number(subTotal);
    const insuranceAmount = insuranceWatch ? Number(((baseForInsurance * 0.0075)).toFixed(2)) : 0;
    const discountedSubTotal = Math.max(0, subTotal - discountAmt);
    const total = Number((discountedSubTotal + vatAmount + insuranceAmount).toFixed(2));
    const signedAmount = (amount) => creditInvoice ? -Math.abs(amount) : amount;
    form.setFieldsValue({
      total: signedAmount(total),
      vatAmount: signedAmount(vatAmount),
      insuranceAmount: signedAmount(insuranceAmount),
    });
  }, [itemsWatch, vatWatch, insuranceWatch, cifValue, discountType, discountValue, form, isCreditInvoice]);

  // Keep monetary fields consistent with status selection in the UI
  useEffect(() => {
    const t = Math.abs(Number(totalWatch || form.getFieldValue('total') || 0));
    const p = Math.abs(Number(paidWatch ?? form.getFieldValue('paid') ?? 0));
    const rem = Math.max(0, Number((t - p).toFixed(2)));

    let expectedStatus = statusWatch;
    if (p > 0 && rem > 0) {
      expectedStatus = "Partially Paid";
    } else if (rem <= 0 && (p > 0 || t > 0)) {
      expectedStatus = "Paid";
    } else if (p === 0) {
      expectedStatus = "Not Paid";
    }

    if (expectedStatus && expectedStatus !== statusWatch) {
      form.setFieldsValue({ status: expectedStatus });
    }
  }, [paidWatch, totalWatch, form]);

  useEffect(() => {
    if ((statusWatch || '').toLowerCase() === 'paid') {
      const t = Math.abs(Number(totalWatch || form.getFieldValue('total') || 0));
      form.setFieldsValue({ paid: t });
    } else if ((statusWatch || '').toLowerCase() === 'not paid') {
      form.setFieldsValue({ paid: 0 });
    }
  }, [statusWatch, form]);

  const handleCopyBillToToServiceRecipient = () => {
    form.setFieldsValue({
      billToText: form.getFieldValue("externalBillToText") || "",
      serviceRecipientText: form.getFieldValue("externalBillToText") || "",
    });
  };

  const handleClearServiceRecipient = () => {
    form.setFieldsValue({
      billToText: "",
      serviceRecipientText: "",
    });
  };

  const onFinish = async (values) => {
    if (!invoice?._id) return;
    try {
      setSubmitting(true);
      const dueDate = values.dueDays
        ? addCalendarDays(values.invoiceDate, Number(values.dueDays)).format("YYYY-MM-DD")
        : invoice.dueDate;

      // Recompute all monetary fields from current form state to ensure correctness
      let paidNum = typeof values.paid === "number" ? values.paid : Number(values.paid || 0);
      const items = values.items || [];
      const creditInvoice = isCreditInvoice || items.some(
        (item) => Number(item?.qty) < 0 || Number(item?.price) < 0
      );
      const subTotal = items.reduce((sum, it) => {
        const qty = Number(it.qty || 0);
        const price = Number(it.price || 0);
        return sum + (creditInvoice ? Math.abs(qty) * Math.abs(price) : qty * price);
      }, 0);
      const vatPercentage = Number(values.vatPercentage || 0);
      const vatAmountAbsolute = vatPercentage > 0 ? Number(((subTotal * vatPercentage) / 100).toFixed(2)) : 0;
      const isInsuranceChecked = !!values.isInsuranceChecked;
      const baseForInsuranceSave = Number(cifValue || 0) > 0 ? Number(cifValue) : Number(subTotal);
      const insuranceAmountAbsolute = isInsuranceChecked ? Number(((baseForInsuranceSave * 0.0075)).toFixed(2)) : 0;
      const discountAmt = invoiceDiscount && Number(invoiceDiscount.amount) > 0 ? Number(invoiceDiscount.amount) : 0;
      const discountedSubTotal = Math.max(0, Number(subTotal) - discountAmt);
      const totalAbsolute = Number((Number(discountedSubTotal) + Number(vatAmountAbsolute) + Number(insuranceAmountAbsolute)).toFixed(2));
      const totalNum = creditInvoice ? -totalAbsolute : totalAbsolute;
      const vatAmount = creditInvoice ? -vatAmountAbsolute : vatAmountAbsolute;
      const insuranceAmount = creditInvoice ? -insuranceAmountAbsolute : insuranceAmountAbsolute;

      // If status is Paid, force paid == total to guarantee zero outstanding
      const statusLower = (values.status || '').toLowerCase();
      if (statusLower === 'paid') {
        paidNum = totalAbsolute;
      }

      // Validation: paid must be between 0 and total
      if (paidNum < 0) {
        toast.error("Paid amount cannot be negative");
        setSubmitting(false);
        return;
      }
      if (paidNum > totalAbsolute) {
        toast.error("Paid amount cannot exceed total amount");
        setSubmitting(false);
        return;
      }
      const remainingAbsolute = Math.max(0, totalAbsolute - Number(paidNum));
      const remaining = creditInvoice ? -remainingAbsolute : remainingAbsolute;

      const payload = {
        ...(creditInvoice ? { isCreditInvoice: true, documentType: "credit" } : {}),
        ...(canEditName ? { name: String(values.name || "").trim() } : {}),
        invoiceDate: values.invoiceDate,
        dueDays: values.dueDays,
        dueDate,
        orderReference: values.orderReference,
        collectionLocation: values.collectionLocation,
        deliveryLocation: values.deliveryLocation,
        billToText: values.billToText,
        serviceRecipientText: values.billToText,
        externalBillToText: values.externalBillToText,
        description: values.description,
        total: isNaN(totalNum) ? invoice.total : totalNum,
        paid: isNaN(paidNum) ? invoice.paid : paidNum,
        remaining,
        status: values.status,
        lastPaymentDate:
          (values.status === "Paid" || values.status === "Partially Paid" || paidNum > 0)
            ? (invoice.lastPaymentDate || new Date().toISOString())
            : (values.status === "Not Paid" && paidNum === 0 ? null : invoice.lastPaymentDate),
        items,
        vatPercentage,
        vatAmount,
        isInsuranceChecked,
        insuranceAmount,
        discount: isCreditInvoice || !invoiceDiscount || Number(invoiceDiscount.amount || 0) <= 0 ? null : invoiceDiscount,
        currency: values.currency || invoice.currency || "USD",
      };
      if (values.invoiceNo && String(values.invoiceNo).trim()) {
        payload.invoiceNo = String(values.invoiceNo).trim();
      }

      // Resolve invoice ID by its unique invoice number, then update
      let targetId = invoice?._id;
      try {
        if (invoice?.invoiceNo) {
          const found = await api.get(`/invoice/search`, { params: { invoiceNo: String(invoice.invoiceNo).trim() } });
          if (found?.data?._id) targetId = found.data._id;
        }
      } catch (_) { /* fall back to provided _id */ }

      await api.put(`/invoice/${targetId}`, payload);

      const invoiceLabel = invoice?.invoiceNo ? `(${invoice.invoiceNo})` : `#${invoice._id}`;
      await api.post("/notifications", {
        userId: invoice.userId,
        title: `Invoice Updated`,
        message: `An invoice ${invoiceLabel} has been updated for your shipment.`,
        link: "/invoices",
        role: "user",
        type: "invoice-edited",
      });

      // Do not update Firestore 'quotes' when editing invoices
      toast.success("Invoice updated");

      // store invoice id to highlight later
      localStorage.setItem("highlightInvoiceNo", invoice._id);

if (onUpdated) onUpdated(invoice._id);

      onClose();
    } catch (err) {
      console.error("Failed to update invoice", err);
      toast.error("Failed to update invoice");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      open={open}
      onCancel={onClose}
      footer={null}
      title={
        <div className="flex items-center gap-2">
          <HiOutlinePencilSquare className="text-[#053F92]" size={22} />
          <Title level={4} className="!mb-0 bg-gradient-to-r from-[#053F92] to-indigo-600 bg-clip-text text-transparent">Update Invoice</Title>
        </div>
      }
      width={900}
      style={{ top: 20 }}
      bodyStyle={{ background: "linear-gradient(180deg, #ffffff 0%, #f8fbff 100%)", paddingTop: 16 }}
      destroyOnClose
      maskClosable={false}
    >
      <Form
        layout="vertical"
        form={form}
        initialValues={initialValues}
        onFinish={onFinish}
        className="[&_.ant-form-item-label>label]:text-slate-700"
      >
        <div className="rounded-lg border border-blue-100 bg-white/70 p-3 mb-4 shadow-sm">
          <p className="text-sm text-slate-600">
            Make changes to the invoice details below. Fields update totals in real-time. Hover items to see actions.
          </p>
        </div>
        <Form.Item label="Invoice No." name="invoiceNo">
          <Input disabled={true} readOnly={true} allowClear autoComplete="off" placeholder="Not assigned" />
        </Form.Item>
        {canEditName && (
          <Form.Item
            label="Name"
            name="name"
            rules={[{ required: true, whitespace: true, message: "Name is required" }]}
          >
            <AutoComplete
              allowClear
              options={nameOptions}
              placeholder={loadingNameOptions ? "Loading names..." : "Select or enter a name"}
              filterOption={(input, option) =>
                String(option?.value || "").toLowerCase().includes(input.toLowerCase())
              }
            />
          </Form.Item>
        )}
        <Form.Item label="Invoice Date" name="invoiceDate" rules={[{ required: true, message: "Invoice date is required" }]}>
          <Input type="date" />
        </Form.Item>
        <Form.Item label="Due Days" name="dueDays" rules={[{ required: true, message: "Due days is required" }]}>
          <InputNumber min={1} className="w-full" />
        </Form.Item>
        <Form.Item
          label="Order Reference"
          name="orderReference"
          rules={[
            { required: true, message: "Order reference is required" },
            { max: ORDER_REFERENCE_MAX_LENGTH, message: `Order reference cannot exceed ${ORDER_REFERENCE_MAX_LENGTH} characters` },
          ]}
        >
          <Input maxLength={ORDER_REFERENCE_MAX_LENGTH} showCount />
        </Form.Item>
        <Form.Item label="Currency" name="currency">
          <Select options={currencyOptions} className="w-full" />
        </Form.Item>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <Form.Item label="Collection" name="collectionLocation">
            <Input placeholder="Collection location" />
          </Form.Item>
          <Form.Item label="Delivery" name="deliveryLocation">
            <Input placeholder="Delivery location" />
          </Form.Item>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <Form.Item
            label="Bill To"
            name="externalBillToText"
            rules={[{ required: true, message: "Bill To is required" }]}
          >
            <Input.TextArea rows={4} maxLength={300} placeholder="Bill to details" />
          </Form.Item>
          <Form.Item
            label={
              <div className="flex w-full items-center justify-between gap-2">
                <span>Service Recipient</span>
                <span className="flex items-center gap-2">
                  <Button size="small" onClick={handleCopyBillToToServiceRecipient}>
                    Copy Customer
                  </Button>
                  <Button size="small" onClick={handleClearServiceRecipient}>
                    Clear
                  </Button>
                </span>
              </div>
            }
            name="billToText"
          >
            <Input.TextArea rows={4} maxLength={300} placeholder="Service recipient details" />
          </Form.Item>
        </div>
        <Form.Item label="Description" name="description" rules={[{ required: true, message: "Description is required" }]}>
          <Input.TextArea rows={3} maxLength={DESCRIPTION_MAX_LENGTH} showCount />
        </Form.Item>

        <Divider orientation="left" className="!border-blue-200">
          <span className="text-[#053F92] font-semibold tracking-wide">Invoice Items</span>
        </Divider>
        <Form.List name="items">
          {(fields, { add, remove }) => (
            <div className="space-y-2">
              {fields.map(({ key, name, ...rest }) => (
                <div key={key} className="grid grid-cols-12 gap-2 items-end rounded-lg border border-blue-100 bg-white/80 p-2 transition-shadow hover:shadow-md">
                  <Form.Item className="col-span-6" {...rest} name={[name, 'description']} label="Description" rules={[{ required: true, message: 'Required' }]}>
                    <Input placeholder="Service description" />
                  </Form.Item>
                  <Form.Item className="col-span-2" {...rest} name={[name, 'qty']} label="Qty" rules={[{ required: true, message: 'Required' }]}>
                    <InputNumber min={0} className="w-full" />
                  </Form.Item>
                  <Form.Item className="col-span-3" {...rest} name={[name, 'price']} label="Price" rules={[{ required: true, message: 'Required' }]}>
                    <InputNumber min={0} step={0.01} className="w-full" />
                  </Form.Item>
                  <Form.Item label=" " colon={false} className="col-span-1">
                    <Button danger onClick={() => remove(name)} className="w-full transition hover:scale-105">Remove</Button>
                  </Form.Item>
                </div>
              ))}
              <Button type="dashed" onClick={() => add({ qty: 1, price: 0 })} block className="!border-blue-300 !text-[#053F92] hover:!border-[#053F92] hover:!text-[#053F92] transition transform hover:scale-[1.01]">
                Add Item
              </Button>
            </div>
          )}
        </Form.List>
        <Divider orientation="left" className="!border-blue-200 mt-5">
          <span className="text-[#053F92] font-semibold tracking-wide">Summary</span>
        </Divider>

        {!isCreditInvoice && (
          <DiscountInput
            discountType={discountType}
            discountValue={discountValue}
            onDiscountTypeChange={(type) => {
              setDiscountType(type);
              setDiscountValue("");
            }}
            onDiscountValueChange={(val) => {
              setDiscountValue(val);
            }}
            onRemove={() => {
              setDiscountValue("");
              setInvoiceDiscount(null);
            }}
            currency={currencyWatch || invoice?.currency || "USD"}
            discountAmount={invoiceDiscount?.amount || 0}
          />
        )}

        <div className="grid grid-cols-1 md:grid-cols-4 gap-3 mt-3">
          <Form.Item label="VAT Percentage" name="vatPercentage">
            <InputNumber className="w-full" min={0} max={100} step={0.01} />
          </Form.Item>
          <Form.Item label="Total VAT" name="vatAmount">
            <InputNumber disabled readOnly className="w-full" min={0} step={0.01} precision={2} />
          </Form.Item>
          <Form.Item name="isInsuranceChecked" valuePropName="checked" label="Insurance">
            <Checkbox>on 0.75% CIF value</Checkbox>
          </Form.Item>
          <Form.Item label="Insurance Amount" name="insuranceAmount">
            <InputNumber disabled readOnly className="w-full" min={0} step={0.01} precision={2} />
          </Form.Item>
          <Form.Item label="Total" name="total">
            <InputNumber disabled={true} readOnly={true} className="w-full font-semibold" min={0} step={0.01} precision={2} />
          </Form.Item>
          <Form.Item
            label="Paid"
            name="paid"
            dependencies={["total"]}
            rules={[
              { required: true, message: "Paid amount is required" },
              {
                validator: (_, value) => {
                  const total = Math.abs(Number(form.getFieldValue("total") || 0));
                  const v = Number(value || 0);
                  if (v < 0) return Promise.reject(new Error("Paid cannot be less than 0"));
                  if (v > total) return Promise.reject(new Error("Paid cannot be greater than Total"));
                  return Promise.resolve();
                },
              },
            ]}
          >
            <InputNumber className="w-full" min={0} step={0.01} />
          </Form.Item>
          <Form.Item label="Status" name="status">
            <Select
              className={`invoice-status-select ${(statusWatch || "").toLowerCase() === "paid"
                  ? "invoice-status-select--paid"
                  : (statusWatch || "").toLowerCase() === "not paid"
                    ? "invoice-status-select--not-paid"
                    : (statusWatch || "").toLowerCase() === "partially paid"
                      ? "invoice-status-select--partially-paid"
                      : ""
                }`}
              popupClassName="invoice-status-dropdown"
              dropdownClassName="invoice-status-dropdown"
              options={[
                { value: "Paid", label: <span className="status-chip status-chip--paid">Paid</span> },
                { value: "Not Paid", label: <span className="status-chip status-chip--not-paid">Not Paid</span> },
                { value: "Partially Paid", label: <span className="status-chip status-chip--partially-paid">Partially Paid</span> },
              ]}
            />
          </Form.Item>
        </div>

        <div className="flex justify-end gap-2 mt-2">
          <Button onClick={onClose} className="transition hover:scale-105">Cancel</Button>
          <Button type="primary" htmlType="submit" loading={submitting} className="bg-gradient-to-r from-[#053F92] to-indigo-600 border-none transition hover:scale-105">Save Changes</Button>
        </div>
      </Form>
    </Modal>
  );
};

export default EditInvoiceModal;
